import type { ContractorScorecard } from "@/hooks/useContractorScorecards";
import { Award, ShieldCheck, Eye, ShieldAlert } from "lucide-react";

export type ReliabilityTier = "Elite" | "Reliable" | "Watch" | "Risk";

export interface ContractorReliability {
  tier: ReliabilityTier;
  score: number; // 0-100
  factors: { label: string; value: string; impact: "positive" | "neutral" | "negative" }[];
}

export function getContractorReliability(scorecard: ContractorScorecard): ContractorReliability {
  const factors: ContractorReliability["factors"] = [];
  let score = 50; // baseline

  // Acceptance rate (0-100, weight: 30)
  const accRate = scorecard.acceptanceRate;
  if (accRate >= 80) { score += 25; factors.push({ label: "Acceptance Rate", value: `${accRate}%`, impact: "positive" }); }
  else if (accRate >= 60) { score += 10; factors.push({ label: "Acceptance Rate", value: `${accRate}%`, impact: "neutral" }); }
  else if (scorecard.totalOpps > 0) { score -= 15; factors.push({ label: "Acceptance Rate", value: `${accRate}%`, impact: "negative" }); }

  // Issue frequency (weight: 25)
  const issueRate = scorecard.issueRate;
  if (scorecard.assignedJobCount > 0) {
    if (issueRate <= 0.1) { score += 20; factors.push({ label: "Issue Rate", value: `${(issueRate * 100).toFixed(0)}%`, impact: "positive" }); }
    else if (issueRate <= 0.3) { score += 5; factors.push({ label: "Issue Rate", value: `${(issueRate * 100).toFixed(0)}%`, impact: "neutral" }); }
    else { score -= 20; factors.push({ label: "Issue Rate", value: `${(issueRate * 100).toFixed(0)}%`, impact: "negative" }); }
  }

  // Credit impact (weight: 20)
  if (scorecard.assignedJobCount > 0) {
    const avgCredit = scorecard.avgCreditPerJob;
    if (avgCredit === 0) { score += 15; factors.push({ label: "Credit Impact", value: "$0 avg", impact: "positive" }); }
    else if (avgCredit <= 200) { score += 0; factors.push({ label: "Credit Impact", value: `$${avgCredit} avg`, impact: "neutral" }); }
    else { score -= 15; factors.push({ label: "Credit Impact", value: `$${avgCredit} avg`, impact: "negative" }); }
  }

  // Quality (weight: 25)
  const quality = scorecard.contractor.rating;
  if (quality >= 4.5) { score += 20; factors.push({ label: "Quality Rating", value: `${quality}/5`, impact: "positive" }); }
  else if (quality >= 3.5) { score += 5; factors.push({ label: "Quality Rating", value: `${quality}/5`, impact: "neutral" }); }
  else { score -= 15; factors.push({ label: "Quality Rating", value: `${quality}/5`, impact: "negative" }); }

  // Clamp
  score = Math.max(0, Math.min(100, score));

  let tier: ReliabilityTier;
  if (score >= 85) tier = "Elite";
  else if (score >= 60) tier = "Reliable";
  else if (score >= 40) tier = "Watch";
  else tier = "Risk";

  return { tier, score, factors };
}

export const RELIABILITY_CONFIG: Record<ReliabilityTier, {
  color: string;
  bg: string;
  border: string;
  icon: typeof Award;
}> = {
  Elite: { color: "text-primary", bg: "bg-primary/10", border: "border-primary/20", icon: Award },
  Reliable: { color: "text-success", bg: "bg-success/10", border: "border-success/20", icon: ShieldCheck },
  Watch: { color: "text-warning", bg: "bg-warning/10", border: "border-warning/20", icon: Eye },
  Risk: { color: "text-destructive", bg: "bg-destructive/10", border: "border-destructive/20", icon: ShieldAlert },
};
